// Admin — Driver create (Wave E).

import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, UserPlus } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Input } from '../../../components/form/Input';
import { Textarea } from '../../../components/form/Textarea';
import { useCreateDriver } from './hooks/useDriversAdmin';
import type { CreateDriverInput } from '../../../types/api';
import styles from './DriverFormPage.module.css';

export function DriverCreatePage(): JSX.Element {
  const navigate = useNavigate();
  const create = useCreateDriver();

  const [name, setName] = useState('');
  const [username, setUsername] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [address, setAddress] = useState('');
  const [postcode, setPostcode] = useState('');
  const [vehicleRegistration, setVehicleRegistration] = useState('');
  const [password, setPassword] = useState('');

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const payload: CreateDriverInput = {
      name: name.trim(),
      phone: phone.trim(),
      username: username.trim(),
      ...(email ? { email: email.trim() } : {}),
      ...(address ? { address: address.trim() } : {}),
      ...(postcode ? { postcode: postcode.trim() } : {}),
      ...(vehicleRegistration.trim() ? { vehicle_registration: vehicleRegistration.trim() } : {}),
      ...(password ? { password } : {}),
    };
    create.mutate(payload, {
      onSuccess: (data) => {
        navigate(`/admin/drivers/${encodeURIComponent(data.uuid)}`);
      },
    });
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title="Add Driver"
        subtitle="Create a new driver login for Dispatch"
        actions={
          <Button
            variant="ghost"
            icon={ArrowLeft}
            onClick={() => navigate('/admin/drivers')}
          >
            Back
          </Button>
        }
      />

      <Card>
        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.row}>
            <Input
              label="Full Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <Input
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              helper="Letters, digits, dot, underscore, dash. Used to log into Dispatch."
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Phone Number"
              variant="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              required
            />
            <Input
              label="Email Address (optional)"
              variant="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Postcode"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
            />
          </div>

          <div className={styles.row}>
            <Input
              label="Vehicle registration"
              value={vehicleRegistration}
              onChange={(e) => setVehicleRegistration(e.target.value)}
              maxLength={10}
            />
          </div>

          <Textarea
            label="Address"
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            rows={3}
          />

          <Input
            label="Initial Password (optional)"
            variant="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            helper="Leave blank to have the server generate one. Min 8 characters."
            autoComplete="new-password"
          />

          <div className={styles.footer}>
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate('/admin/drivers')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              icon={UserPlus}
              loading={create.isPending}
            >
              Create Driver
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
